import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { buildLoginPath } from "../utils/authRedirect";
import useAppDispatch from "hooks/useAppDispatch";
import {
  forgotComplete,
  forgotStart,
} from "../../../../redux/features/user/userSlice";
import { isEmail, isValidPassword } from "utils/validation";
import { getApiErrorMessage } from "utils/errors";

const PASSWORD_MIN_LENGTH = 8;
const RESEND_COOLDOWN_MS = 60 * 1000;

function isFailedAuthResult(result) {
  if (result?.status === 401) return true;
  const error = result?.error;
  return typeof error === "string" ? error.length > 0 : Boolean(error);
}

function normalizeEmail(value) {
  return String(value ?? "").trim();
}

function getServerSuccessMessage(result, fallback) {
  if (typeof result === "string" && result.trim() && result !== "Error") {
    return result.trim();
  }
  if (result && typeof result === "object") {
    if (typeof result.message === "string" && result.message.trim()) {
      return result.message.trim();
    }
    if (typeof result.detail === "string" && result.detail.trim()) {
      return result.detail.trim();
    }
  }
  return fallback;
}

export default function useForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams();
  const dispatch = useAppDispatch();

  const [isCodeSent, setIsCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState(false);
  const [newPasswordRepeat, setNewPasswordRepeat] = useState("");
  const [errorNewPasswordRepeat, setErrorNewPasswordRepeat] = useState(false);
  const [isPasswordChanged, setIsPasswordChanged] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [completeError, setCompleteError] = useState("");
  const [serverSuccessMessage, setServerSuccessMessage] = useState("");
  const [completeSuccessMessage, setCompleteSuccessMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [resendLocked, setResendLocked] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const resendUnlockTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (resendUnlockTimerRef.current) {
        clearTimeout(resendUnlockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendSeconds(0);
      return undefined;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setResendSeconds(left);
      if (left === 0) {
        setResendLocked(false);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendAvailableAt]);

  const { title, description } = useMemo(() => {
    if (code) {
      return {
        title: t("forgotPassword.createTitle"),
        description: t("forgotPassword.createHint"),
      };
    }
    if (isCodeSent) {
      return {
        title: t("forgotPassword.sentTitle"),
        description: "",
      };
    }
    return {
      title: t("forgotPassword.passRecovery"),
      description: t("forgotPassword.passRecoveryHint"),
    };
  }, [code, isCodeSent, t]);

  const getPasswordError = useCallback(
    (value) => {
      if (!value) {
        return t("forgotPassword.enterPass");
      }
      if (value.length < PASSWORD_MIN_LENGTH) {
        return t("forgotPassword.passLeast");
      }
      if (!isValidPassword(value)) {
        return t("forgotPassword.latinChars");
      }
      return false;
    },
    [t]
  );

  const sendResetEmail = useCallback(async () => {
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail !== email) {
      setEmail(normalizedEmail);
    }

    if (!normalizedEmail) {
      setErrorEmail(t("forgotPassword.enterEmail"));
      return false;
    }
    if (!isEmail(normalizedEmail)) {
      setErrorEmail(t("forgotPassword.invalidEmail"));
      return false;
    }

    setErrorEmail(false);
    setServerError(false);
    setServerSuccessMessage("");
    setIsSending(true);

    try {
      const result = await dispatch(forgotStart({ email: normalizedEmail }));
      if (isFailedAuthResult(result)) {
        setServerError(
          getApiErrorMessage(result, t("forgotPassword.connectErr"))
        );
        return false;
      }

      setServerSuccessMessage(
        getServerSuccessMessage(
          result,
          t("forgotPassword.sentMessage", { email: normalizedEmail })
        )
      );
      setIsCodeSent(true);
      setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
      setResendLocked(true);
      if (resendUnlockTimerRef.current) {
        clearTimeout(resendUnlockTimerRef.current);
      }
      resendUnlockTimerRef.current = setTimeout(() => {
        setResendLocked(false);
      }, RESEND_COOLDOWN_MS);
      return true;
    } catch (err) {
      setServerError(getApiErrorMessage(err, t("forgotPassword.connectErr")));
      return false;
    } finally {
      setIsSending(false);
    }
  }, [dispatch, email, t]);

  const doForgotStart = useCallback(() => {
    if (isSending) return;
    sendResetEmail();
  }, [isSending, sendResetEmail]);

  const doResendCode = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      if (isSending) return;
      if (resendAvailableAt && Date.now() < resendAvailableAt) {
        return;
      }
      sendResetEmail();
    },
    [isSending, resendAvailableAt, sendResetEmail]
  );

  const doForgotComplete = useCallback(() => {
    if (isCompleting || isPasswordChanged) return;

    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setErrorNewPassword(passwordError);
      return;
    }

    if (!newPasswordRepeat) {
      setErrorNewPasswordRepeat(t("forgotPassword.repeatPass"));
      return;
    }
    if (newPassword !== newPasswordRepeat) {
      setErrorNewPasswordRepeat(t("forgotPassword.passNotMatch"));
      return;
    }

    setErrorNewPassword(false);
    setErrorNewPasswordRepeat(false);
    setCompleteError("");
    setIsCompleting(true);

    dispatch(
      forgotComplete({
        code,
        password: newPassword,
      })
    )
      .then((result) => {
        if (isFailedAuthResult(result)) {
          setCompleteError(
            getApiErrorMessage(result, t("forgotPassword.recLinkInvalid"))
          );
          setIsLinkInvalid(true);
          return;
        }

        setCompleteSuccessMessage(
          getServerSuccessMessage(result, t("forgotPassword.passChanged"))
        );
        setIsPasswordChanged(true);
      })
      .catch((err) => {
        setCompleteError(
          getApiErrorMessage(err, t("forgotPassword.recLinkInvalid"))
        );
        setIsLinkInvalid(true);
      })
      .finally(() => {
        setIsCompleting(false);
      });
  }, [
    code,
    dispatch,
    getPasswordError,
    isCompleting,
    isPasswordChanged,
    newPassword,
    newPasswordRepeat,
    t,
  ]);

  const validateEmail = useCallback(
    (value) => {
      const normalized = normalizeEmail(value);
      if (normalized.length > 0 && !isEmail(normalized)) {
        setErrorEmail(t("forgotPassword.invalidEmail"));
      } else {
        setErrorEmail(false);
        setServerError(false);
      }
    },
    [t]
  );

  const validatePassword = useCallback(
    (value) => {
      setErrorNewPassword(getPasswordError(value));
    },
    [getPasswordError]
  );

  const validatePasswordRepeat = useCallback(
    (value) => {
      if (value.length === 0) {
        setErrorNewPasswordRepeat(t("forgotPassword.repeatPass"));
      } else if (value !== newPassword) {
        setErrorNewPasswordRepeat(t("forgotPassword.passNotMatch"));
      } else {
        setErrorNewPasswordRepeat(false);
      }
    },
    [newPassword, t]
  );

  const completeErrorMessage = completeError;
  const loginPath = buildLoginPath({ search: location.search, email });
  const goToSignIn = useCallback(() => {
    navigate(loginPath);
  }, [navigate, loginPath]);

  return {
    t,
    code,
    title,
    description,
    email,
    errorEmail,
    newPassword,
    errorNewPassword,
    newPasswordRepeat,
    errorNewPasswordRepeat,
    serverError,
    setServerError,
    isCodeSent,
    isPasswordChanged,
    isSending,
    isCompleting,
    resendLocked,
    resendSeconds,
    isLinkInvalid,
    showPassword,
    setShowPassword,
    goToSignIn,
    completeErrorMessage,
    completeSuccessMessage,
    serverSuccessMessage,
    setEmail,
    setErrorEmail,
    setNewPassword,
    setErrorNewPassword,
    setNewPasswordRepeat,
    setErrorNewPasswordRepeat,
    doResendCode,
    loginPath,
    doForgotStart,
    doForgotComplete,
    validateEmail,
    validatePassword,
    validatePasswordRepeat,
  };
}
